import useOrderStatus from "@repo/shopify/hooks/useOrderStatus";
import type { ILineItem } from "@repo/shopify/src/api/fetchOrder";
import type { IOrdersEdgesNode } from "@repo/shopify/src/queries/customer-gql";
import { extractId } from "@repo/shopify/src/utils";
import {
  OrderStatusButtonType,
  OrderStatusTrans,
  checkDrawOrder,
  orderHasTracking,
} from "@repo/shopify/src/utils/order";
import { transformDate, transformWon } from "@repo/utils/src/number";
import type { Image as ShopifyImage } from "@shopify/hydrogen-react/storefront-api-types";
import type { Maybe } from "graphql/jsutils/Maybe";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import Image from "@components/atoms/Image";
import Status from "@components/atoms/Status";

import OrderStatusButton from "@molecules/OrderStatusButton";
import TrackingNumber from "@molecules/TrackingNumber";

import { URLS } from "@const/urls";

import { getStatusColor } from "@utils/order";

interface IProps {
  id: string;
  name: string;
  orderCreatedAt: string;
  image: Maybe<ShopifyImage>;
  lineTitle: string;
  totalPrice: number;
  order: IOrdersEdgesNode;
}

function OrderListItem({ totalPrice, order }: IProps) {
  const { t } = useTranslation();
  const { activeButton, orderStatus } = useOrderStatus(order, true);

  const lineItems = useMemo(() => {
    if (!order.lineItems.edges.length) return;

    return order.lineItems.edges.map(({ node }, index) => {
      return {
        id: node?.variant?.id ?? index,
        image: node.variant
          ? node.variant.image
          : { height: undefined, width: undefined, url: order.featuredImage?.value },
        title: node?.variant?.product.title ?? node.title,
      } as ILineItem;
    });
  }, [order.lineItems.edges]);

  return (
    <div className="relative flex cursor-pointer justify-between gap-2.5">
      <Link className="absolute inset-0 z-0" to={`${URLS.ACCOUNT_ORDER}/${extractId(order.id)}`} />

      <div className="pointer-events-none z-10 flex max-w-6/10 flex-col gap-2">
        <span className="font-regular text-secondary inline-block text-sm">{transformDate(order.processedAt)}</span>
        <div className="flex flex-col gap-1">
          <Status
            orderName={order.name.replace("#", "")}
            statusColor={getStatusColor(orderStatus)}
            statusName={t(OrderStatusTrans[orderStatus])}
          />
          <span>
            {t("account.order.amount")}: {transformWon(totalPrice)}
            {t("cart.currency")}
          </span>
          <div>
            <div>{order.isOffline?.value ? "구매처: 성수 스토어" : "구매처: 온라인 스토어"}</div>
          </div>
          {/* 인보이스가 있는 주문일 때만 TrackingNumber 컴포넌트를 보여준다. */}
          {orderHasTracking(order) && <TrackingNumber order={order} />}
        </div>
        {/* 주문취소 및 반품신청 버튼 */}
        {activeButton !== OrderStatusButtonType.NONE && !checkDrawOrder(order) ? (
          <div className="pointer-events-auto">
            <OrderStatusButton
              amount={order.totalPrice.amount}
              buttonType={activeButton}
              id={order.id}
              name={order.name}
            />
          </div>
        ) : null}
      </div>
      <div className="bg-tertiary pointer-events-none z-10 aspect-4/5 h-fit w-1/3 overflow-hidden rounded-sm">
        {lineItems?.[0].image?.url ? (
          <Image
            alt={lineItems?.[0].title}
            height={lineItems?.[0].image.height ?? 1350}
            src={lineItems?.[0].image.url}
            width={lineItems?.[0].image.width ?? 1080}
          />
        ) : null}
      </div>
    </div>
  );
}

export default OrderListItem;
